// Renderer Canvas2D — porte simplificado de synapse-os/renderer.js (812 linhas).
// Mantém: nebula bg, parallax stars, blob orgânico com sine-wave perturbation,
// bezier links com fase animada, glow neon por nó, cores via CSS vars (themes).

import type { GraphLink, GraphNode } from '@synapse/engine';
import { ha } from '../utils/constants.js';

export interface Camera { x: number; y: number; zoom: number; }

export interface RemoteCursorRender {
  clientId: number;
  name: string | null;
  color: string;
  worldX: number;
  worldY: number;
  ts: number;
}

export interface RenderState {
  ctx: CanvasRenderingContext2D;
  width: number;
  height: number;
  cam: Camera;
  nodes: GraphNode[];
  links: GraphLink[];
  selectedId: string | null;
  hoveredId: string | null;
  hoveredLinkId?: string | null;
  selectedLinkId?: string | null;
  multiSelected: Set<string>;
  time: number;
  stars: Star[];
  themeVars: ThemeVars;
  remoteCursors?: RemoteCursorRender[];
  remoteSelections?: Map<string, string>; // nodeId → collaborator color
  linkingFrom?: string | null;
  linkDragPos?: { x: number; y: number } | null;
  searchQuery?: string;
  snapToGrid?: boolean;
  knifePath?: { x: number; y: number }[]; // world-space points; renderer projects to screen.
  noteEditingId?: string | null; // node currently being annotated — skip rendering its blob.
  noteCardBounds?: { left: number; top: number; width: number; height: number } | null;
  marquee?: { x1: number; y1: number; x2: number; y2: number } | null; // screen coords
  clickPulse?: { id: string; start: number } | null; // performance.now() do clique
}

export function getMoveHandle(node: GraphNode, cam: Camera, w: number, h: number): { sx: number; sy: number } {
  const { sx, sy } = w2s(node.x, node.y, cam, w, h);
  const r = (node.size || 22) * cam.zoom;
  return { sx, sy: sy - r - 14 };
}

export function getGroupHandle(node: GraphNode, cam: Camera, w: number, h: number): { sx: number; sy: number } {
  const { sx, sy } = w2s(node.x, node.y, cam, w, h);
  const r = (node.size || 22) * cam.zoom;
  return { sx: sx + r + 16, sy: sy - r - 6 };
}

export interface Star {
  x: number; y: number; z: number; // z = parallax depth (0.3 - 1.0)
  baseAlpha: number;
  pulse: number;
}

export interface ThemeVars {
  canvasBg: string;
  nebulaCore: string;
  nebulaEdge: string;
  nebulaGlow: string;
  starColor: string;
  border: string;
  label: string;
}

export function readThemeVars(): ThemeVars {
  const cs = getComputedStyle(document.documentElement);
  return {
    canvasBg: cs.getPropertyValue('--canvas-bg').trim() || '#020408',
    nebulaCore: cs.getPropertyValue('--nebula-core').trim() || 'rgba(8,12,18,1)',
    nebulaEdge: cs.getPropertyValue('--nebula-edge').trim() || 'rgba(2,4,8,1)',
    nebulaGlow: cs.getPropertyValue('--nebula-glow').trim() || 'rgba(0,18,38,0.22)',
    starColor: cs.getPropertyValue('--star-color').trim() || 'rgba(195,218,255,1)',
    border: cs.getPropertyValue('--border').trim() || 'rgba(0,245,255,0.12)',
    label: cs.getPropertyValue('--text').trim() || 'rgba(240,246,255,0.95)',
  };
}

export function generateStars(count = 320): Star[] {
  const stars: Star[] = [];
  for (let i = 0; i < count; i++) {
    stars.push({
      // Stored as offsets in a virtual 3000x3000 tile. Renderer wraps via modulo
      // against the live viewport so the field never empties on pan/zoom.
      x: Math.random() * 3000,
      y: Math.random() * 3000,
      z: 0.3 + Math.random() * 0.7,
      baseAlpha: 0.25 + Math.random() * 0.55,
      pulse: Math.random() * Math.PI * 2,
    });
  }
  return stars;
}

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
type ImgEntry = { img: HTMLImageElement; ready: boolean };
const imageCache = new Map<string, ImgEntry>();

export function getNodeImage(content: string | undefined): HTMLImageElement | null {
  if (!content || typeof content !== 'string') return null;
  const trimmed = content.trim();
  if (!trimmed) return null;
  const src = UUID_RE.test(trimmed) ? `/api/assets/${trimmed}/serve` : trimmed;
  let entry = imageCache.get(src);
  if (!entry) {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    entry = { img, ready: false };
    imageCache.set(src, entry);
    img.onload = () => { entry!.ready = true; };
    img.onerror = () => { imageCache.delete(src); };
    img.src = src;
  }
  return entry.ready ? entry.img : null;
}

export function w2s(wx: number, wy: number, cam: Camera, w: number, h: number) {
  return {
    sx: (wx + cam.x) * cam.zoom + w / 2,
    sy: (wy + cam.y) * cam.zoom + h / 2,
  };
}

export function s2w(sx: number, sy: number, cam: Camera, w: number, h: number) {
  return {
    wx: (sx - w / 2) / cam.zoom - cam.x,
    wy: (sy - h / 2) / cam.zoom - cam.y,
  };
}

// Raio do blob num ângulo específico — mesma fórmula do traceBlob, usada pra
// ancorar arestas exatamente na borda orgânica do nó.
function blobRadiusAt(
  baseR: number, a: number, time: number, zoom: number, seedKey: string,
): number {
  const seed = (seedKey.charCodeAt(0) || 0) * 0.29 + (seedKey.charCodeAt(1) || 0) * 0.11;
  return (baseR
    + Math.sin(a * 3 + time * 1.85 + seed) * 2.9
    + Math.cos(a * 5.2 - time * 1.08 + seed) * 2.2
    + Math.sin(a * 7 + time * 2.35 - seed) * 1.2
  ) * zoom;
}

// Blob orgânico: r base + soma de sines em frequências diferentes (perturbação).
function traceBlob(
  ctx: CanvasRenderingContext2D,
  cx: number, cy: number, baseR: number,
  time: number, zoom: number, seedKey: string,
) {
  const steps = 40;
  ctx.beginPath();
  for (let i = 0; i <= steps; i++) {
    const a = (i / steps) * Math.PI * 2;
    const r = blobRadiusAt(baseR, a, time, zoom, seedKey);
    const px = cx + Math.cos(a) * r;
    const py = cy + Math.sin(a) * r;
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.closePath();
}

// Dimensões em tela do card de imagem (proporção original, lado maior = 2*size).
function imageBoxDims(
  n: { size: number }, img: HTMLImageElement, zoom: number,
): { dw: number; dh: number } | null {
  const iw = img.naturalWidth;
  const ih = img.naturalHeight;
  if (!iw || !ih) return null;
  const maxDim = Math.max(8, (n.size || 22) * 2 * zoom);
  return iw >= ih
    ? { dw: maxDim, dh: (maxDim * ih) / iw }
    : { dh: maxDim, dw: (maxDim * iw) / ih };
}

// Raio efetivo do nó na direção `angle` (em tela): retângulo pra nós de imagem,
// blob orgânico pros demais. Usado pelas arestas pra encostar na borda real.
function nodeEdgeRadiusAt(
  n: GraphNode, angle: number, time: number, zoom: number,
): number {
  if (n.type === 'image') {
    const img = getNodeImage(n.content);
    if (img) {
      const d = imageBoxDims(n, img, zoom);
      if (d) {
        const pad = 4 * zoom;
        const rx = d.dw / 2 + pad;
        const ry = d.dh / 2 + pad;
        const c = Math.abs(Math.cos(angle));
        const s = Math.abs(Math.sin(angle));
        return Math.min(c > 1e-6 ? rx / c : Infinity, s > 1e-6 ? ry / s : Infinity);
      }
    }
  }
  return blobRadiusAt(n.size || 22, angle, time, zoom, n.id);
}

function roundRectPath(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, w: number, h: number, r: number,
) {
  const rr = Math.max(0, Math.min(r, w / 2, h / 2));
  ctx.beginPath();
  ctx.moveTo(x + rr, y);
  ctx.arcTo(x + w, y, x + w, y + h, rr);
  ctx.arcTo(x + w, y + h, x, y + h, rr);
  ctx.arcTo(x, y + h, x, y, rr);
  ctx.arcTo(x, y, x + w, y, rr);
  ctx.closePath();
}

function drawBackground(state: RenderState) {
  const { ctx, width, height, themeVars } = state;
  // Nebula gradiente radial.
  const grad = ctx.createRadialGradient(
    width / 2, height / 2, 0,
    width / 2, height / 2, Math.max(width, height) / 1.2,
  );
  grad.addColorStop(0, themeVars.nebulaCore);
  grad.addColorStop(0.55, themeVars.nebulaEdge);
  grad.addColorStop(1, themeVars.canvasBg);
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, width, height);
  // Glow soft.
  ctx.fillStyle = themeVars.nebulaGlow;
  ctx.fillRect(0, 0, width, height);
}

function drawStars(state: RenderState) {
  const { ctx, cam, width, height, time, stars, themeVars } = state;
  // Stars live in a virtual 3000x3000 tile and are wrapped to the viewport via
  // modulo each frame so panning never reveals an empty void.
  const margin = 200;
  const TILE_W = width + margin * 2;
  const TILE_H = height + margin * 2;
  // Ambient current keeps the field visibly alive even with the camera still.
  const driftX = Math.sin(time * 0.11) * 36 + Math.sin(time * 0.27) * 8;
  const driftY = Math.cos(time * 0.085) * 28 + Math.cos(time * 0.22) * 6;
  const baseColor = themeVars.starColor; // e.g. "rgba(195, 218, 255, 0.3)"

  for (const s of stars) {
    // Parallax: deeper (lower z) drifts slower; cam.zoom modulates pan distance.
    const parallaxX = -cam.x * cam.zoom * s.z * 0.6 + driftX * s.z;
    const parallaxY = -cam.y * cam.zoom * s.z * 0.6 + driftY * s.z;
    const rawX = s.x + parallaxX;
    const rawY = s.y + parallaxY;
    const sx = ((rawX % TILE_W) + TILE_W) % TILE_W - margin;
    const sy = ((rawY % TILE_H) + TILE_H) % TILE_H - margin;
    if (sx < -margin || sx > width + margin || sy < -margin || sy > height + margin) continue;

    const twinkle = 0.5 + 0.5 * Math.sin(time * 1.3 + s.pulse);
    const alpha = Math.min(1, s.baseAlpha * (0.18 + 0.32 * twinkle));
    const coreR = (1 - s.z) * 0.55 + 0.3;
    const haloR = coreR * 2.4;

    // Diffuse halo — soft glow only, keeps stars unobtrusive.
    ctx.fillStyle = baseColor.replace(/[\d.]+\)$/, `${(alpha * 0.14).toFixed(3)})`);
    ctx.beginPath();
    ctx.arc(sx, sy, haloR, 0, Math.PI * 2);
    ctx.fill();

    // Bright core.
    ctx.fillStyle = baseColor.replace(/[\d.]+\)$/, `${alpha.toFixed(3)})`);
    ctx.beginPath();
    ctx.arc(sx, sy, coreR, 0, Math.PI * 2);
    ctx.fill();
  }
}

// If `px,py` is inside `rect`, extend toward `qx,qy` until exit; return the exit
// point pushed slightly outward so the link visually stops at the card border.
function clipExitAtRect(
  px: number, py: number, qx: number, qy: number,
  rect: { left: number; top: number; width: number; height: number },
): { sx: number; sy: number } | null {
  const x1 = rect.left, y1 = rect.top, x2 = rect.left + rect.width, y2 = rect.top + rect.height;
  if (px < x1 || px > x2 || py < y1 || py > y2) return null;
  const dx = qx - px, dy = qy - py;
  let tBest = Infinity;
  if (dx > 0) {
    const t = (x2 - px) / dx; const y = py + t * dy;
    if (y >= y1 && y <= y2 && t < tBest) tBest = t;
  } else if (dx < 0) {
    const t = (x1 - px) / dx; const y = py + t * dy;
    if (y >= y1 && y <= y2 && t < tBest) tBest = t;
  }
  if (dy > 0) {
    const t = (y2 - py) / dy; const x = px + t * dx;
    if (x >= x1 && x <= x2 && t < tBest) tBest = t;
  } else if (dy < 0) {
    const t = (y1 - py) / dy; const x = px + t * dx;
    if (x >= x1 && x <= x2 && t < tBest) tBest = t;
  }
  if (!isFinite(tBest)) return null;
  const dist = Math.hypot(dx, dy) || 1;
  const tExt = tBest + 4 / dist;
  return { sx: px + tExt * dx, sy: py + tExt * dy };
}

function drawLinks(state: RenderState) {
  const { ctx, links, nodes, cam, width, height, time, selectedId, noteEditingId, noteCardBounds } = state;
  const byId = new Map(nodes.map((n) => [n.id, n]));
  const skipPulse = cam.zoom < 0.35;
  for (const lk of links) {
    const a = byId.get(lk.source);
    const b = byId.get(lk.target);
    if (!a || !b) continue;
    const cA = w2s(a.x, a.y, cam, width, height);
    const cB = w2s(b.x, b.y, cam, width, height);
    const dx = cB.sx - cA.sx;
    const dy = cB.sy - cA.sy;
    const len = Math.hypot(dx, dy) || 1;
    const offset = 22;
    const phase = Math.sin(time * 0.6 + a.x * 0.01) * 0.5 + 0.5;
    const cpX = (cA.sx + cB.sx) / 2 + (-dy / len) * offset;
    const cpY = (cA.sy + cB.sy) / 2 + (dx / len) * offset;

    // Ancora a linha exatamente na borda real de cada nó (blob orgânico ou
    // card de imagem), na direção do ponto de controle da bezier.
    const dxA = cpX - cA.sx; const dyA = cpY - cA.sy; const lenA = Math.hypot(dxA, dyA) || 1;
    const rA = Math.max(2, nodeEdgeRadiusAt(a, Math.atan2(dyA, dxA), time, cam.zoom) + 1);
    let A = { sx: cA.sx + (dxA/lenA)*rA, sy: cA.sy + (dyA/lenA)*rA };
    const dxB = cpX - cB.sx; const dyB = cpY - cB.sy; const lenB = Math.hypot(dxB, dyB) || 1;
    const rB = Math.max(2, nodeEdgeRadiusAt(b, Math.atan2(dyB, dxB), time, cam.zoom) + 1);
    let B = { sx: cB.sx + (dxB/lenB)*rB, sy: cB.sy + (dyB/lenB)*rB };

    // If one endpoint is the note-editing node, the inline card replaces the blob.
    // Trim the link so it stops at the card border instead of disappearing under it.
    if (noteCardBounds && noteEditingId) {
      if (a.id === noteEditingId) {
        const exit = clipExitAtRect(A.sx, A.sy, cpX, cpY, noteCardBounds);
        if (exit) A = exit;
      }
      if (b.id === noteEditingId) {
        const exit = clipExitAtRect(B.sx, B.sy, cpX, cpY, noteCardBounds);
        if (exit) B = exit;
      }
    }

    const isSel = selectedId === a.id || selectedId === b.id;

    if (lk.type === 'wiki') {
      // Wiki: dashed violet — static, no pulse
      const isLinkHovered = state.hoveredLinkId === lk.id;
      const isLinkSelected = state.selectedLinkId === lk.id;
      const alpha = (isSel ? 0.75 : 0.28) + 0.12 * phase;
      ctx.strokeStyle = isLinkSelected ? '#ff4d4d' : (isLinkHovered ? ha('#a78bfa', 0.8) : ha('#a78bfa', alpha));
      ctx.lineWidth = isLinkSelected ? 2.5 : (isLinkHovered ? 2.0 : (isSel ? 1.6 : 1.0));
      ctx.setLineDash([5, 4]);
      ctx.beginPath();
      ctx.moveTo(A.sx, A.sy);
      ctx.quadraticCurveTo(cpX, cpY, B.sx, B.sy);
      ctx.stroke();
      ctx.setLineDash([]);
      
      if (isLinkSelected) {
        ctx.fillStyle = '#ff4d4d';
        ctx.beginPath();
        ctx.arc(cpX, cpY, 12, 0, Math.PI * 2);
        ctx.fill();
        ctx.font = `14px "Material Symbols Outlined"`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#ffffff';
        ctx.fillText('close', cpX, cpY);
      }
    } else {
      // Synapse: myelin sheath style — segmented thick bezier with bright bands and axon underneath.
      const isLinkHovered = state.hoveredLinkId === lk.id;
      const isLinkSelected = state.selectedLinkId === lk.id;

      const zoom = cam.zoom;
      const baseW = (isLinkSelected ? 4 : isLinkHovered ? 3.6 : isSel ? 3.2 : 2.8) * Math.max(0.6, Math.min(1.4, zoom));
      const segLen = Math.max(12, 24 * zoom);
      const gapLen = Math.max(5, 10 * zoom);
      const dashPhase = (time * 14) % (segLen + gapLen);

      // Gradiente ao longo da ligação: cor do nó A até a cor do nó B, com a
      // transição concentrada no meio. Selecionada fica vermelha sólida.
      const linkStroke = (alpha: number): string | CanvasGradient => {
        if (isLinkSelected) return ha('#ff4d4d', alpha);
        if (a.color === b.color) return ha(a.color, alpha);
        const g = ctx.createLinearGradient(A.sx, A.sy, B.sx, B.sy);
        g.addColorStop(0, ha(a.color, alpha));
        g.addColorStop(0.35, ha(a.color, alpha));
        g.addColorStop(0.65, ha(b.color, alpha));
        g.addColorStop(1, ha(b.color, alpha));
        return g;
      };

      // Underlying axon (thin, dim) — visible through gaps.
      ctx.strokeStyle = linkStroke(isSel ? 0.55 : 0.30);
      ctx.lineWidth = Math.max(1.2, baseW * 0.18);
      ctx.lineCap = 'round';
      ctx.setLineDash([]);
      ctx.beginPath();
      ctx.moveTo(A.sx, A.sy);
      ctx.quadraticCurveTo(cpX, cpY, B.sx, B.sy);
      ctx.stroke();

      // Outer glow halo of segments.
      ctx.strokeStyle = linkStroke(isLinkHovered ? 0.32 : 0.18);
      ctx.lineWidth = baseW + 8;
      ctx.lineCap = 'round';
      ctx.setLineDash([segLen, gapLen]);
      ctx.lineDashOffset = -dashPhase;
      ctx.beginPath();
      ctx.moveTo(A.sx, A.sy);
      ctx.quadraticCurveTo(cpX, cpY, B.sx, B.sy);
      ctx.stroke();

      // Main myelin body — saturated color.
      ctx.strokeStyle = linkStroke(isSel ? 0.92 : 0.78);
      ctx.lineWidth = baseW;
      ctx.lineCap = 'round';
      ctx.setLineDash([segLen, gapLen]);
      ctx.lineDashOffset = -dashPhase;
      ctx.beginPath();
      ctx.moveTo(A.sx, A.sy);
      ctx.quadraticCurveTo(cpX, cpY, B.sx, B.sy);
      ctx.stroke();

      // Inner highlight — bright center stripe gives capsule depth.
      ctx.strokeStyle = 'rgba(255,255,255,0.42)';
      ctx.lineWidth = Math.max(1.4, baseW * 0.42);
      ctx.lineCap = 'round';
      ctx.setLineDash([Math.max(8, segLen * 0.55), gapLen + segLen * 0.45]);
      ctx.lineDashOffset = -dashPhase - segLen * 0.22;
      ctx.beginPath();
      ctx.moveTo(A.sx, A.sy);
      ctx.quadraticCurveTo(cpX, cpY, B.sx, B.sy);
      ctx.stroke();

      ctx.setLineDash([]);
      ctx.lineDashOffset = 0;

      if (isLinkSelected) {
        ctx.fillStyle = '#ff4d4d';
        ctx.beginPath();
        ctx.arc(cpX, cpY, 12, 0, Math.PI * 2);
        ctx.fill();
        ctx.font = `14px "Material Symbols Outlined"`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillStyle = '#ffffff';
        ctx.fillText('close', cpX, cpY);
      }
    }
  }
}

function drawGrid(state: RenderState) {
  const { ctx, cam, width, height } = state;
  const gridSize = 40;
  const tl = s2w(0, 0, cam, width, height);
  const br = s2w(width, height, cam, width, height);
  const startX = Math.floor(tl.wx / gridSize) * gridSize;
  const endX = Math.ceil(br.wx / gridSize) * gridSize;
  const startY = Math.floor(tl.wy / gridSize) * gridSize;
  const endY = Math.ceil(br.wy / gridSize) * gridSize;
  const dotR = Math.max(0.4, Math.min(1.5, 0.7 * cam.zoom));
  ctx.fillStyle = 'rgba(0,245,255,0.18)';
  for (let wx = startX; wx <= endX; wx += gridSize) {
    for (let wy = startY; wy <= endY; wy += gridSize) {
      const { sx, sy } = w2s(wx, wy, cam, width, height);
      ctx.beginPath();
      ctx.arc(sx, sy, dotR, 0, Math.PI * 2);
      ctx.fill();
    }
  }
}

function drawNodes(state: RenderState) {
  const { ctx, nodes, cam, width, height, time, selectedId, hoveredId, multiSelected, searchQuery, linkingFrom, remoteSelections, noteEditingId } = state;
  const hasSearch = !!searchQuery && searchQuery.trim().length > 0;
  const LOD_SIMPLE = cam.zoom < 0.2;
  const margin = 80;

  for (const n of nodes) {
    const { sx, sy } = w2s(n.x, n.y, cam, width, height);
    // Viewport cull — skip nodes fully outside canvas (with glow margin).
    if (sx < -margin || sx > width + margin || sy < -margin || sy > height + margin) continue;

    const baseR = (n.size || 22);
    const isSel = n.id === selectedId;
    const isHover = n.id === hoveredId;
    const isMulti = multiSelected.has(n.id);
    const isNoteEditing = n.id === noteEditingId;
    const c = n.color || '#00f5ff';
    const isMatch = hasSearch && n.label.toLowerCase().includes(searchQuery!.toLowerCase());
    const isDimmed = hasSearch && !isMatch;

    if (isDimmed) ctx.globalAlpha = 0.22;

    // Note editing: HTML card replaces the node entirely. Skip blob/glow/icon/label
    // — InlineNoteEditor draws everything (border, title, textarea) in DOM.
    if (isNoteEditing) {
      if (isDimmed) ctx.globalAlpha = 1;
      continue;
    }
    const renderR = baseR;
    const suppressIcon = false;

    // LOD_SIMPLE: plain circle dot — no blob, no gradients, no labels.
    if (LOD_SIMPLE) {
      ctx.fillStyle = ha(c, isSel ? 0.9 : 0.6);
      ctx.beginPath();
      ctx.arc(sx, sy, Math.max(2, baseR * cam.zoom), 0, Math.PI * 2);
      ctx.fill();
      if (isSel) {
        ctx.strokeStyle = c;
        ctx.lineWidth = 1.5;
        ctx.stroke();
      }
      if (isDimmed) ctx.globalAlpha = 1;
      continue;
    }

    // Search match: pulsing gold halo.
    if (isMatch) {
      const pulse = 0.55 + 0.45 * Math.sin(time * 3.5);
      const searchR = baseR * cam.zoom * 2.8;
      const sg = ctx.createRadialGradient(sx, sy, 0, sx, sy, searchR);
      sg.addColorStop(0, `rgba(255,215,0,${0.55 * pulse})`);
      sg.addColorStop(1, 'rgba(255,215,0,0)');
      ctx.fillStyle = sg;
      ctx.beginPath();
      ctx.arc(sx, sy, searchR, 0, Math.PI * 2);
      ctx.fill();
    }

    // Nó de imagem: card retangular com proporção original + membrana fina.
    // Substitui blob/halo/ícone — desenha tudo e pula pro próximo nó.
    const cardImg = n.type === 'image' && cam.zoom > 0.2 ? getNodeImage(n.content) : null;
    const cardDims = cardImg ? imageBoxDims(n, cardImg, cam.zoom) : null;
    if (cardImg && cardDims) {
      const { dw, dh } = cardDims;
      const pad = 4 * cam.zoom;
      const bx = sx - dw / 2 - pad;
      const by = sy - dh / 2 - pad;
      const bw = dw + pad * 2;
      const bh = dh + pad * 2;
      const rad = Math.min(10 * cam.zoom, bw * 0.18, bh * 0.18);

      // Glow suave atrás do card.
      const glowRImg = Math.hypot(bw, bh) * 0.62 * (isSel ? 1.25 : 1);
      const haloImg = ctx.createRadialGradient(sx, sy, 0, sx, sy, glowRImg);
      haloImg.addColorStop(0, ha(c, isSel ? 0.4 : 0.2));
      haloImg.addColorStop(1, ha(c, 0));
      ctx.fillStyle = haloImg;
      ctx.beginPath();
      ctx.arc(sx, sy, glowRImg, 0, Math.PI * 2);
      ctx.fill();

      // Membrana: contorno fino acompanhando o retângulo da imagem.
      roundRectPath(ctx, bx, by, bw, bh, rad);
      ctx.fillStyle = ha(c, 0.10);
      ctx.fill();
      ctx.strokeStyle = isMulti ? '#ffffff' : ha(c, isSel ? 0.95 : isHover ? 0.85 : 0.65);
      ctx.lineWidth = isSel ? 1.8 : isHover ? 1.3 : 0.9;
      ctx.stroke();

      // Imagem com proporção original (contain).
      ctx.save();
      roundRectPath(ctx, sx - dw / 2, sy - dh / 2, dw, dh, Math.max(2, rad * 0.7));
      ctx.clip();
      ctx.drawImage(cardImg, sx - dw / 2, sy - dh / 2, dw, dh);
      ctx.restore();

      // Anel de seleção remota (colaborador).
      const remoteColorImg = remoteSelections?.get(n.id);
      if (remoteColorImg) {
        ctx.strokeStyle = remoteColorImg;
        ctx.lineWidth = 2;
        ctx.setLineDash([4, 3]);
        roundRectPath(ctx, bx - 5, by - 5, bw + 10, bh + 10, rad + 4);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      // Lock indicator.
      if (n.locked) {
        ctx.fillStyle = '#ffcc00';
        ctx.beginPath();
        ctx.arc(bx + bw - 4, by + 4, 3, 0, Math.PI * 2);
        ctx.fill();
      }

      // Label abaixo do card.
      if (cam.zoom > 0.35) {
        const labelFont = Math.max(11, Math.min(64, baseR * 0.62 * cam.zoom));
        const labelOffset = bh / 2 + Math.max(12, labelFont * 0.9);
        ctx.fillStyle = state.themeVars.label;
        ctx.font = `600 ${labelFont}px Outfit, ui-sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        const label = n.label.length > 26 ? n.label.slice(0, 25) + '…' : n.label;
        ctx.fillText(label, sx, sy + labelOffset);
      }

      // Hint de link no hover.
      if (isHover && !linkingFrom) {
        ctx.strokeStyle = ha(c, 0.35);
        ctx.lineWidth = 1.2;
        ctx.setLineDash([3, 3]);
        roundRectPath(ctx, bx - 6, by - 6, bw + 12, bh + 12, rad + 5);
        ctx.stroke();
        ctx.setLineDash([]);
      }

      if (isDimmed) ctx.globalAlpha = 1;
      continue;
    }

    // Outer glow halo (multiplicado por seleção).
    const glowR = renderR * cam.zoom * (isSel ? 2.2 : isHover ? 1.7 : 1.4);
    const halo = ctx.createRadialGradient(sx, sy, 0, sx, sy, glowR);
    halo.addColorStop(0, ha(c, isSel ? 0.55 : 0.30));
    halo.addColorStop(1, ha(c, 0));
    ctx.fillStyle = halo;
    ctx.beginPath();
    ctx.arc(sx, sy, glowR, 0, Math.PI * 2);
    ctx.fill();

    // Blob orgânico.
    traceBlob(ctx, sx, sy, renderR, time, cam.zoom, n.id);
    // Fill com gradient interno.
    const inner = ctx.createRadialGradient(sx, sy, 0, sx, sy, renderR * cam.zoom);
    inner.addColorStop(0, ha(c, 0.85));
    inner.addColorStop(0.6, ha(c, 0.50));
    inner.addColorStop(1, ha(c, 0.18));
    ctx.fillStyle = inner;
    ctx.fill();

    // Borda.
    ctx.strokeStyle = isMulti ? '#ffffff' : ha(c, isSel ? 1.0 : 0.8);
    ctx.lineWidth = isSel ? 2.8 : isHover ? 1.8 : 1.2;
    ctx.stroke();

    // Remote selection ghost ring.
    const remoteColor = remoteSelections?.get(n.id);
    if (remoteColor) {
      ctx.strokeStyle = remoteColor;
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 3]);
      ctx.beginPath();
      ctx.arc(sx, sy, (renderR + 8) * cam.zoom, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    // Ícone (nós de imagem com card são tratados acima e nunca chegam aqui).
    if (!suppressIcon && n.icon && cam.zoom > 0.25) {
      // Icon (material-symbols ligature) — only when no image.
      const iconSize = Math.max(10, Math.min(22, (baseR * cam.zoom) * 0.72));
      ctx.font = `${iconSize}px "Material Symbols Outlined"`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillStyle = ha(c, 0.9);
      ctx.fillText(n.icon, sx, sy);
    }

    // Lock indicator.
    if (n.locked) {
      ctx.fillStyle = '#ffcc00';
      ctx.beginPath();
      ctx.arc(sx + renderR * cam.zoom * 0.7, sy - renderR * cam.zoom * 0.7, 3, 0, Math.PI * 2);
      ctx.fill();
    }

    // Label — font + offset scale with node size so giant nodes get giant labels.
    if (cam.zoom > 0.35) {
      const labelFont = Math.max(11, Math.min(64, baseR * 0.62 * cam.zoom));
      const labelOffset = renderR * cam.zoom + Math.max(12, labelFont * 0.9);
      ctx.fillStyle = state.themeVars.label;
      ctx.font = `600 ${labelFont}px Outfit, ui-sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const label = n.label.length > 26 ? n.label.slice(0, 25) + '…' : n.label;
      ctx.fillText(label, sx, sy + labelOffset);
    }

    // Link hint ring — appears on hover
    if (isHover && !linkingFrom) {
      ctx.strokeStyle = ha(c, 0.35);
      ctx.lineWidth = 1.2;
      ctx.setLineDash([3, 3]);
      ctx.beginPath();
      ctx.arc(sx, sy, (renderR + 6) * cam.zoom, 0, Math.PI * 2);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    if (isDimmed) ctx.globalAlpha = 1;
  }
}

function drawLinkingDrag(state: RenderState) {
  const { ctx, linkingFrom, linkDragPos, nodes, cam, width, height, time } = state;
  if (!linkingFrom || !linkDragPos) return;
  const a = nodes.find((n) => n.id === linkingFrom);
  if (!a) return;
  const A = w2s(a.x, a.y, cam, width, height);
  ctx.strokeStyle = ha(a.color, 0.7 + 0.3 * Math.sin(time * 4));
  ctx.lineWidth = 1.6;
  ctx.setLineDash([6, 4]);
  ctx.beginPath();
  ctx.moveTo(A.sx, A.sy);
  ctx.lineTo(linkDragPos.x, linkDragPos.y);
  ctx.stroke();
  ctx.setLineDash([]);
}

function drawRemoteCursors(state: RenderState) {
  const { ctx, remoteCursors, cam, width, height } = state;
  if (!remoteCursors?.length) return;
  const now = Date.now();
  for (const c of remoteCursors) {
    const age = now - c.ts;
    if (age > 30_000) continue;
    const fade = Math.max(0.2, 1 - age / 30_000);
    const { sx, sy } = w2s(c.worldX, c.worldY, cam, width, height);
    ctx.save();
    ctx.translate(sx, sy);
    ctx.fillStyle = c.color;
    ctx.globalAlpha = fade;
    ctx.beginPath();
    ctx.moveTo(0, 0);
    ctx.lineTo(14, 6);
    ctx.lineTo(6, 14);
    ctx.closePath();
    ctx.fill();
    if (c.name) {
      ctx.font = '11px Outfit, ui-sans-serif';
      const w = ctx.measureText(c.name).width + 12;
      ctx.fillStyle = c.color;
      ctx.fillRect(16, 4, w, 18);
      ctx.fillStyle = '#0a0a0f';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText(c.name, 22, 8);
    }
    ctx.restore();
  }
}

function drawKnifePath(state: RenderState) {
  const { ctx, knifePath, cam, width, height, time } = state;
  if (!knifePath || knifePath.length < 2) return;
  ctx.save();
  // Pulsing red glow trail.
  const pulse = 0.7 + 0.3 * Math.sin(time * 8);
  ctx.strokeStyle = `rgba(255, 80, 80, ${0.85 * pulse})`;
  ctx.lineWidth = 2.4;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.shadowColor = 'rgba(255, 60, 60, 0.85)';
  ctx.shadowBlur = 8;
  ctx.beginPath();
  for (let i = 0; i < knifePath.length; i++) {
    const p = knifePath[i]!;
    const { sx, sy } = w2s(p.x, p.y, cam, width, height);
    if (i === 0) ctx.moveTo(sx, sy);
    else ctx.lineTo(sx, sy);
  }
  ctx.stroke();
  // Bright leading head dot.
  const head = knifePath[knifePath.length - 1]!;
  const { sx, sy } = w2s(head.x, head.y, cam, width, height);
  ctx.shadowBlur = 14;
  ctx.fillStyle = '#ff6464';
  ctx.beginPath();
  ctx.arc(sx, sy, 4, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

// Retângulo de seleção (marquee, estilo Figma).
function drawMarquee(state: RenderState) {
  const { ctx, marquee } = state;
  if (!marquee) return;
  const x = Math.min(marquee.x1, marquee.x2);
  const y = Math.min(marquee.y1, marquee.y2);
  const w = Math.abs(marquee.x2 - marquee.x1);
  const h = Math.abs(marquee.y2 - marquee.y1);
  if (w < 2 && h < 2) return;
  ctx.fillStyle = 'rgba(0,245,255,0.07)';
  ctx.fillRect(x, y, w, h);
  ctx.strokeStyle = 'rgba(0,245,255,0.65)';
  ctx.lineWidth = 1;
  ctx.setLineDash([4, 3]);
  ctx.strokeRect(x, y, w, h);
  ctx.setLineDash([]);
}

// Pulso ao clicar num nó — feedback do delay de clique simples vs duplo.
function drawClickPulse(state: RenderState) {
  const { ctx, clickPulse, nodes, cam, width, height } = state;
  if (!clickPulse) return;
  const age = performance.now() - clickPulse.start;
  const DUR = 340;
  if (age > DUR) return;
  const n = nodes.find((nd) => nd.id === clickPulse.id);
  if (!n) return;
  const { sx, sy } = w2s(n.x, n.y, cam, width, height);
  const t = age / DUR;
  const r = (n.size || 22) * cam.zoom * (1 + t * 0.9);
  ctx.strokeStyle = ha(n.color || '#00f5ff', 0.7 * (1 - t));
  ctx.lineWidth = 2.2 * (1 - t) + 0.6;
  ctx.beginPath();
  ctx.arc(sx, sy, r, 0, Math.PI * 2);
  ctx.stroke();
}

export function render(state: RenderState): void {
  drawBackground(state);
  drawStars(state);
  if (state.snapToGrid) drawGrid(state);
  drawLinks(state);
  drawLinkingDrag(state);
  drawNodes(state);
  drawClickPulse(state);
  drawKnifePath(state);
  drawMarquee(state);
  drawRemoteCursors(state);
}
